import { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Radar, Sparkles, LogIn } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { RecaptchaBlock } from "../components/RecaptchaBlock";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export function TurnstileWidget({ onToken }) {
  if (!SITE_KEY) return null;
  return <Turnstile siteKey={SITE_KEY} onSuccess={onToken} onError={() => onToken(null)} onExpire={() => onToken(null)} />;
}

export default function Login() {
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState("login"); // login | 2fa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [tsToken, setTsToken] = useState(null);
  const [captcha, setCaptcha] = useState(null);
  const captchaRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/captcha-config").then((r) => setCaptcha(r.data)).catch(() => setCaptcha({ enabled: false }));
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") {
        let tokens = { v2: null, v3: null };
        if (captcha?.enabled && captchaRef.current) {
          tokens = await captchaRef.current.getTokens();
          if (captcha.v2_site_key && !tokens.v2) { setError("Marca la casilla «No soy un robot»"); return; }
        }
        const { data } = await api.post("/auth/login", { email, password, turnstile_token: tsToken, recaptcha_v2_token: tokens.v2, recaptcha_v3_token: tokens.v3 });
        if (data.requires_2fa) { setTempToken(data.temp_token); setMode("2fa"); }
        else setUser(data);
      } else if (mode === "2fa") {
        const { data } = await api.post("/auth/2fa/verify", { temp_token: tempToken, code });
        setUser(data);
      }
    } catch (err) {
      setError(apiError(err));
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const titles = { login: "Bienvenido", "2fa": "Verificación en dos pasos" };
  const subtitles = {
    login: "Accede a tu radar financiero.",
    "2fa": "Introduce el código de tu app Authenticator o un código de recuperación.",
  };

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row text-left">
      <div className="hidden lg:flex flex-1 bg-primary text-primary-foreground flex-col justify-between p-12">
        <div className="flex items-center gap-2">
          <Radar size={24} strokeWidth={2.5} />
          <span className="font-heading font-extrabold tracking-tight text-lg">RADAR FINANCIERO</span>
        </div>
        <div>
          <h1 className="font-heading text-5xl font-extrabold tracking-tighter leading-tight max-w-md">
            Decisiones financieras en menos de 30 segundos.
          </h1>
          <p className="mt-6 text-white/60 max-w-sm">
            Patrimonio, deudas, pagos pendientes y proyecciones futuras de tu familia. En un solo lugar.
          </p>
        </div>
        <div className="text-xs text-white/40 tracking-[0.2em] uppercase">Inteligencia financiera familiar</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 lg:hidden mb-10">
            <Radar size={22} strokeWidth={2.5} />
            <span className="font-heading font-extrabold tracking-tight">RADAR FINANCIERO</span>
          </div>
          <h2 className="font-heading text-3xl font-extrabold tracking-tight">{titles[mode]}</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-8">{subtitles[mode]}</p>
          <form onSubmit={submit} className="space-y-4">
            {mode === "login" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="login-email" data-testid="login-email-label">User data</Label>
                  <Input id="login-email" data-testid="login-email-input" type="email" required placeholder="xxxxxxx" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="login-password" data-testid="login-password-label">Authorization Code</Label>
                  <Input id="login-password" data-testid="login-password-input" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" />
                </div>
              </>
            )}
            {mode === "2fa" && (
              <Input data-testid="twofa-code-input" inputMode="numeric" autoFocus placeholder="Código de 6 dígitos" value={code} onChange={(e) => setCode(e.target.value)} className="text-center text-xl tracking-[0.3em]" />
            )}
            <TurnstileWidget onToken={setTsToken} />
            {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full h-11">
              {loading ? "Un momento…" : mode === "login" ? <><LogIn size={16} className="mr-2" /> Entrar</> : "Verificar"}
            </Button>
            {mode === "login" && <RecaptchaBlock ref={captchaRef} config={captcha} />}
          </form>

          {mode !== "login" && (
            <div className="mt-6 flex flex-col gap-2 text-sm">
              <button data-testid="back-to-login" onClick={() => { setMode("login"); setError(""); setCode(""); }} className="text-muted-foreground hover:text-foreground text-left">← Volver al inicio de sesión</button>
            </div>
          )}

          {mode === "login" && (
            <div className="mt-8 border-t border-border pt-6" data-testid="access-info-block">
              <p className="text-xs text-muted-foreground leading-relaxed">
                El acceso a Radar Financiero se obtiene mediante la contratación de un plan. Consulta nuestros planes disponibles para recibir tus credenciales de acceso.
              </p>
              <Link to="/planes" data-testid="ver-planes-button" className="mt-4 block">
                <Button variant="outline" className="w-full h-11 font-semibold">
                  <Sparkles size={15} className="mr-2" /> Ver planes
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
